import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Home, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPropertyDetail = () => {
  const { orgId, propertyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPropertyData();
  }, [orgId, propertyId]);

  const getToken = () => localStorage.getItem('access_token');

  const fetchPropertyData = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/orgs/${orgId}/properties/${propertyId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load property data');
    } finally {
      setLoading(false);
    }
  };

  const toggleRoomDeleted = async (unitId, isDeleted) => {
    const action = isDeleted ? 'delete' : 'restore';
    if (!window.confirm(`Are you sure you want to ${action} this room?`)) return;

    try {
      await axios.patch(
        `${API}/admin/orgs/${orgId}/properties/${propertyId}/units/${unitId}`,
        { is_deleted: isDeleted },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Room ${isDeleted ? 'deleted' : 'restored'} successfully`);
      fetchPropertyData();
    } catch (error) {
      toast.error(`Failed to ${action} room`);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!data) return <div className="p-6">Property not found</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate(`/admin-org/${orgId}`)}>
          <ArrowLeft size={16} className="mr-2" />
          Back to Organization
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-slate-900">Property: {data.property.name}</h1>
        <p className="text-slate-600 mt-1">{data.property.address}</p>
        <div className="flex gap-3 mt-2 text-sm text-slate-500">
          <span>{data.unit_types.length} room types</span>
          <span>{data.units.filter(u => !u.is_deleted).length} active rooms</span>
          <span>{data.units.filter(u => u.is_deleted).length} deleted rooms</span>
          <span>{data.reservations_summary.total} reservations</span>
        </div>
      </div>

      {/* Rooms Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home size={20} />
            Rooms ({data.units.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.units.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Room Name</th>
                    <th className="text-left p-2">Type</th>
                    <th className="text-left p-2">Floor</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Deleted?</th>
                    <th className="text-left p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.units.map((unit) => {
                    const unitType = data.unit_types.find(ut => ut.id === unit.unit_type_id);
                    return (
                      <tr key={unit.id} className="border-t">
                        <td className="p-2 font-medium">{unit.name}</td>
                        <td className="p-2">{unitType?.name || 'N/A'}</td>
                        <td className="p-2">{unit.floor}</td>
                        <td className="p-2">
                          <span className="text-xs bg-slate-100 px-2 py-1 rounded">{unit.status}</span>
                        </td>
                        <td className="p-2">
                          {unit.is_deleted ? (
                            <span className="text-red-600 text-xs">
                              Yes {unit.deleted_at && `(${unit.deleted_at.split('T')[0]})`}
                            </span>
                          ) : (
                            <span className="text-green-600 text-xs">No</span>
                          )}
                        </td>
                        <td className="p-2">
                          {unit.is_deleted ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleRoomDeleted(unit.id, false)}
                            >
                              <RotateCcw size={14} className="mr-1" />
                              Restore
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => toggleRoomDeleted(unit.id, true)}
                            >
                              <Trash2 size={14} className="mr-1" />
                              Mark Deleted
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No rooms configured</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPropertyDetail;